import { useContext, useEffect, useMemo, useState } from 'react';
import { TRole } from 'models/auth';
import Quickstart from 'components/Quickstart';
import quickstartStyles from 'components/Quickstart/quickstart.module.sass';
import Lightbulb from 'components/Quickstart/icons/lightbulb';
import { ContentfulEntry } from 'blades/widgetTypes';
import { IQuickstartMenu } from 'models/Quickstart/@types';
import QuickstartModel from 'models/Quickstart';
import QuickstartContext from './context';
import AuthContext from '../AuthProvider';

const getChatbotButtonPosition = () => {
  const flowise = document.querySelector('flowise-chatbot');
  const shadowRoot = flowise?.shadowRoot;
  if (shadowRoot) {
    const chatbotButton = shadowRoot.querySelector('button:has(svg)');
    if (chatbotButton) {
      return chatbotButton.getBoundingClientRect();
    }
  }
  return null;
};

const getQuickstartButtonPosition = () => {
  const chatbotButton = getChatbotButtonPosition();
  const body = document.querySelector('body') as HTMLBodyElement;
  if (!body) {
    return null;
  }
  const bodyWidth = body.getBoundingClientRect().width;
  const windowHeight = window.innerHeight;

  if (chatbotButton) {
    // get right and bottom of the chatbot button
    const right = bodyWidth - chatbotButton.right;
    const bottom = windowHeight - chatbotButton.bottom;
    return { right, bottom, chatbotButton };
  }
  return null;
};

export default function QuickstartProvider({ children }: { children: React.ReactNode }) {
  const { appUser } = useContext(AuthContext);
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [quickstartButtonPosition, setQuickstartButtonPosition] = useState<{
    right: number;
    bottom: number;
  } | null>(null);
  const [hasChatbotButton, setHasChatbotButton] = useState<boolean>(false);

  const [quickStart, setQuickStart] = useState<ContentfulEntry<IQuickstartMenu> | null>(null);

  useEffect(() => {
    const fetchQuickStart = async () => {
      if (appUser?.role === TRole.TEACHER) {
        const result = await QuickstartModel.getQuickstartMenu('teacher');
        if (result.length > 0) {
          setQuickStart(result[0]);
        }
      }
    };
    fetchQuickStart();
  }, [appUser?.role]);

  useEffect(() => {
    let count = 0;
    const MAX_COUNT = 20;
    const interval = setInterval(() => {
      const quickstartButtonPosition = getQuickstartButtonPosition();
      if (quickstartButtonPosition) {
        clearInterval(interval);
        setHasChatbotButton(true);
      } else {
        count += 1;
        if (count >= MAX_COUNT) {
          clearInterval(interval);
        }
      }
    }, 500);
  }, []);

  useEffect(() => {
    if (hasChatbotButton) {
      const quickstartButtonPosition = getQuickstartButtonPosition();
      if (quickstartButtonPosition) {
        setQuickstartButtonPosition({
          right: quickstartButtonPosition.right + quickstartButtonPosition.chatbotButton.width + 4,
          bottom: quickstartButtonPosition.bottom,
        });
      }
    }
  }, [hasChatbotButton]);

  const closeQuickstart = () => {
    setIsOpen(false);
  };

  const value = useMemo(() => ({ quickStart, isOpen, closeQuickstart }), [quickStart, isOpen]);

  if (!quickStart) {
    return children;
  }

  return (
    <QuickstartContext.Provider value={value}>
      <button
        type="button"
        onClick={() => setIsOpen(true)}
        className={quickstartStyles.quickstartOpenButton}
        style={{
          right: `${quickstartButtonPosition?.right}px`,
          bottom: `${quickstartButtonPosition?.bottom}px`,
        }}
      >
        <span className={quickstartStyles.icon}>
          <Lightbulb />
        </span>
        <span className={quickstartStyles.buttonText}>Quickstart</span>
      </button>
      <Quickstart />
      {children}
    </QuickstartContext.Provider>
  );
}
